import { toCanonical, createPointArray, computeFacialSignals } from './faceMath'
import { lerp, FACE_MESH_POINT_COUNT, LANDMARKS } from './helpers'

export function createCanonicalTracker() {
  let liveCanonicalSmoothed = null
  return function processCanonical({
    lastFaceLandmarks,
    faceMetrics,
    canvasWidth,
    canvasHeight,
    avatarRig,
    settings,
    baselineCanonical,
    baselineFrames,
    mouthLevel = 0
  }) {
    let liveCanonical = null
    if (!lastFaceLandmarks || !faceMetrics)
      return {
        liveCanonical,
        baselineCanonical,
        baselineFrames,
        facialSignals: { jawOpen: 0, blinkLeft: 0, blinkRight: 0 }
      }

    const liveCount = Math.min(
      lastFaceLandmarks.length,
      FACE_MESH_POINT_COUNT,
      avatarRig?.meshCount || FACE_MESH_POINT_COUNT
    )
    const rawCanonical = toCanonical(
      lastFaceLandmarks,
      faceMetrics,
      canvasWidth,
      canvasHeight,
      liveCount
    )
    if (rawCanonical) {
      if (!liveCanonicalSmoothed || liveCanonicalSmoothed.length !== liveCount) {
        liveCanonicalSmoothed = rawCanonical.map((p) => ({ x: p.x, y: p.y, z: p.z || 0 }))
      } else {
        const baseAlpha =
          settings.meshQuality === 'high'
            ? 0.52
            : settings.meshQuality === 'performance'
              ? 0.34
              : 0.44
        const smoothing = Number.isFinite(Number(settings.canonicalSmoothing))
          ? Math.min(0.95, Math.max(0, Number(settings.canonicalSmoothing)))
          : null
        const alpha = smoothing == null ? baseAlpha : Math.min(1, Math.max(0.08, 1 - smoothing))
        for (let i = 0; i < liveCount; i += 1) {
          liveCanonicalSmoothed[i].x =
            liveCanonicalSmoothed[i].x * (1 - alpha) + rawCanonical[i].x * alpha
          liveCanonicalSmoothed[i].y =
            liveCanonicalSmoothed[i].y * (1 - alpha) + rawCanonical[i].y * alpha
          liveCanonicalSmoothed[i].z =
            liveCanonicalSmoothed[i].z * (1 - alpha) + (rawCanonical[i].z || 0) * alpha
        }
      }
      liveCanonical = liveCanonicalSmoothed
    }

    const facialSignalsPre = liveCanonical
      ? computeFacialSignals(liveCanonical, baselineCanonical)
      : { jawOpen: 0, blinkLeft: 0, blinkRight: 0 }

    // Baseline capture & adaptive smoothing (expression baseline)
    if (settings.expressionWarp && avatarRig && liveCanonical) {
      const turnAmount = Math.abs(
        (liveCanonical[LANDMARKS.leftEye]?.z || 0) - (liveCanonical[LANDMARKS.rightEye]?.z || 0)
      )
      const speaking = mouthLevel > 0.22 || (facialSignalsPre?.jawOpen || 0) > 0.16
      const frontalEnoughForCapture = turnAmount < 0.055
      const frontalEnoughForAdapt = turnAmount < 0.07
      if (!baselineCanonical || baselineCanonical.length !== liveCanonical.length) {
        // Avoid locking a side-turned pose as neutral.
        if (frontalEnoughForCapture && !speaking) {
          baselineCanonical = createPointArray(liveCanonical.length)
          for (let i = 0; i < liveCanonical.length; i += 1) {
            baselineCanonical[i].x = liveCanonical[i].x
            baselineCanonical[i].y = liveCanonical[i].y
            baselineCanonical[i].z = liveCanonical[i].z || 0
          }
          baselineFrames = 0
        } else {
          baselineCanonical = null
          baselineFrames = 0
        }
      } else {
        let deltaEnergy = 0
        for (let i = 0; i < liveCanonical.length; i += 1) {
          deltaEnergy +=
            Math.abs(liveCanonical[i].x - baselineCanonical[i].x) +
            Math.abs(liveCanonical[i].y - baselineCanonical[i].y)
        }
        deltaEnergy /= Math.max(1, liveCanonical.length)
        const adapting =
          !speaking && frontalEnoughForAdapt && (baselineFrames < 14 || deltaEnergy < 0.065)
        const alpha = baselineFrames < 14 ? (adapting ? 0.2 : 0) : adapting ? 0.018 : 0
        if (alpha > 0) {
          for (let i = 0; i < liveCanonical.length; i += 1) {
            baselineCanonical[i].x = lerp(baselineCanonical[i].x, liveCanonical[i].x, alpha)
            baselineCanonical[i].y = lerp(baselineCanonical[i].y, liveCanonical[i].y, alpha)
            baselineCanonical[i].z = lerp(
              baselineCanonical[i].z || 0,
              liveCanonical[i].z || 0,
              alpha
            )
          }
          baselineFrames = Math.min(240, baselineFrames + 1)
        }
      }
    }

    const facialSignals = liveCanonical
      ? computeFacialSignals(liveCanonical, baselineCanonical)
      : { jawOpen: 0, blinkLeft: 0, blinkRight: 0 }

    return { liveCanonical, baselineCanonical, baselineFrames, facialSignals }
  }
}

export default { createCanonicalTracker }
